import { useState, useEffect } from 'react';
import SaveButtonWithModal from './SaveButtonWithModel';

const stakeholderCategories = [
  {
    id: 'ALL',
    title: 'ALL',
    description: 'Select all stakeholder categories at once'
  },
  {
    id: 'Government Agencies',
    title: 'Government Agencies',
    description: 'Ministries and local government entities that provide policy support, funding, and formalization services for entrepreneurs',
    challenges: 'Challenges include bureaucratic delays, complex application processes, and limited funding availability'
  },
  {
    id: 'Development Partners & Donor Agencies',
    title: 'Development Partners & Donor Agencies',
    description: 'International organizations that fund entrepreneurship programs, grants, and training initiatives',
    challenges: 'Funding cycles can be unpredictable, and requirements may be stringent with competitive application processes'
  },
  {
    id: 'Business Support Organizations',
    title: 'Business Support Organizations',
    description: 'Entities offering incubation, training, networking, and acceleration services to startups and SMEs',
    challenges: 'Limited capacity to serve all entrepreneurs, with some programs being urban-centric and excluding rural businesses'
  },
  {
    id: 'Financial Institutions',
    title: 'Financial Institutions',
    description: 'Banks, microfinance institutions, and alternative lenders that provide capital to entrepreneurs',
    challenges: 'High collateral requirements, stringent credit assessments, and limited products tailored for early-stage businesses'
  },
  {
    id: 'Offtake & Market Partners',
    title: 'Offtake & Market Partners',
    description: 'Corporates, retailers, and platforms that connect entrepreneurs to markets and supply chain opportunities',
    challenges: 'Difficult entry requirements, large minimum order quantities, and payment terms that strain cash flow'
  },
  {
    id: 'NGOs & Nonprofits',
    title: 'NGOs & Nonprofits',
    description: 'Organizations providing targeted support to specific entrepreneur groups (youth, women, rural)',
    challenges: 'Limited scale, short-term project funding cycles, and sometimes restrictive eligibility criteria'
  },
  {
    id: 'Training & Academic Institutions',
    title: 'Training & Academic Institutions',
    description: 'Schools and programs offering digital skills, business management, and technical training',
    challenges: 'Curriculum may not always match market needs, and practical entrepreneurship training can be lacking'
  },
  {
    id: 'Regulatory & Compliance Bodies',
    title: 'Regulatory & Compliance Bodies',
    description: 'Entities responsible for business licensing, safety standards, and quality assurance',
    challenges: 'Complex compliance requirements, high costs of certification, and lengthy approval processes'
  },
  {
    id: 'Technology & Innovation Stakeholders',
    title: 'Technology & Innovation Stakeholders',
    description: 'Organizations fostering digital inclusion, innovation, and tech entrepreneurship',
    challenges: 'Limited infrastructure in some regions, high costs of technology adoption, and skills gaps'
  },
  {
    id: 'Others',
    title: 'Others',
    description: 'Additional stakeholder groups not covered by the main categories'
  },
  {
    id: '-',
    title: '-',
    description: ''
  }
];

const GroupExperience = ({ selectedExperience = [], setSelectedExperience, onSave }) => {
  // Default selections - first two options selected by default
  const defaultSelections = ['ALL', 'Government Agencies'];
  const [hoveredItem, setHoveredItem] = useState(null);
  const [referralInput, setReferralInput] = useState('');
  const [isReferralSelected, setIsReferralSelected] = useState(false);

  const handleReferralToggle = () => {
    setIsReferralSelected(!isReferralSelected);
    if (!isReferralSelected) {
      setReferralInput(''); // Clear input when enabling referral
    }
  };

  
  // Initialize state with default selections
  const [internalSelected, setInternalSelected] = useState(defaultSelections);
  const [isExpanded, setIsExpanded] = useState(false);
  
  const selected = selectedExperience !== undefined ? 
    (selectedExperience.length > 0 ? selectedExperience : defaultSelections) : 
    internalSelected;

  // Set default selections on first render
  useEffect(() => {
    if (selectedExperience === undefined) {
      setInternalSelected(defaultSelections);
    }
    if (selectedExperience !== undefined && selectedExperience.length === 0) {
      setSelectedExperience(defaultSelections);
    }
  }, []);

  const handleSelect = (category) => {
    let newSelected;
    
    // Special handling for "ALL" selection
    if (category === 'ALL') {
      newSelected = selected.includes('ALL') 
        ? [] 
        : stakeholderCategories.map(item => item.id);
    } else {
      newSelected = selected.includes(category)
        ? selected.filter(item => item !== category)
        : [...selected, category];
      
      // If "ALL" was selected and user selects another item, remove "ALL"
      if (newSelected.includes('ALL') && category !== 'ALL') {
        newSelected = newSelected.filter(item => item !== 'ALL');
      }
    }
    
    // Update parent state if setter is provided
    if (setSelectedExperience) {
      setSelectedExperience(newSelected);
    } else {
      setInternalSelected(newSelected);
    }
    
    // Call onSave callback if provided
    if (onSave) {
      onSave({
        typeOfGroupExperience: 'Stakeholder Categories',
        selectedExperience: newSelected
      });
    }
  };

  const handleSave = () => {
    if (onSave) {
      onSave({
        typeOfGroupExperience: 'Stakeholder Categories',
        selectedExperience: selected,
        referralCode: isReferralSelected ? referralInput.trim() : null
      });
    }
  };
  
  return (
    <div className='md:mt-4'>
      {/* Heading */}
      <div 
        className="w-full md:h-[88px] h-[40px] flex items-center justify-between bg-[#152C24]  rounded-[10px] md:mt-7 mt-3 relative cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="w-[154px] h-[40px] sm:w-[100px] sm:h-[100px]"></div>
        <div className="absolute md:text-[32px] text-left px-4 text-white text-[8px] font-bold">
          DATA
        </div>
        <div className="md:w-[154px] md:h-[88px] w-[80px] h-[40px] bg-[#D9D9D9] rounded-r-[10px] flex items-center justify-center ">
          <img
            src="https://res.cloudinary.com/dzngpgki4/image/upload/v1748785688/Expand_down_yilqh4.png"
            alt="expand icon"
            className={`md:w-12 md:h-12 w-3 h-3 transition-all duration-300 ease-in-out ${
              isExpanded ? 'transform rotate-180' : ''
            }`}
          />
        </div>
      </div>
      
      {/* Selectable Buttons */}
      {isExpanded  && (
        <div>
          <p className="md:text-[24px] text-[8px] italic font-medium leading-[1.10] text-center md:mt-6 mt-1 md:mb-4 ">
            Please select the registered and verified users within the MyHustle ecosystem  <br/>who are allowed to view your data
          </p>
          <div className="grid grid-cols-2 md:gap-1 gap-1 md:mt-5 mt-2">
            {stakeholderCategories.map((category, i) => {
              const isActive = selected.includes(category.id);
              const isHovered = hoveredItem === i;
              
              return (
                <div
                  key={category.id}
                  className="relative"
                  onMouseEnter={() => setHoveredItem(i)}
                  onMouseLeave={() => setHoveredItem(null)}
                >
                  <button
                    onClick={() => handleSelect(category.id)}
                    className={`
                      flex flex-col justify-between
                      w-full text-left
                      bg-[#F5F5F5]
                      border border-[#F5F5F5] rounded-[5px]
                      md:px-4 px-2
                      overflow-hidden
                      transition-all duration-300 ease-in-out
                      ${isHovered ? 'md:h-[200px] h-[80px]' : 'md:h-[80px] h-[20px]'}
                    `}
                  >
                    <div className="flex md:justify-center justify-between mt-1 items-center w-full md:mt-3">
                      <span className="text-[4px] md:text-[20px] md:w-full font-bold leading-[1.10]">
                        {category.title}
                      </span>
                      <span
                        className={`
                          md:w-14 md:h-14 w-3 h-3 rounded-full flex-shrink-0
                          ${isActive 
                            ? 'bg-[#25242C] md:border-[6px] border-[3px] border-white'
                            : 'md:border-[6px] border-[3px] border-white bg-[#D9D9D9]'}
                        `}
                      />
                    </div>
                    
                    {isHovered && (
                      <div className="md:mt-2 mt-1 transition-opacity duration-200">
                        <p className="text-[4px] md:text-[18px] font-normal">
                          {category.description}
                        </p>
                        {category.challenges && (
                          <p className="text-[4px] md:text-[18px] font-normal mt-1">
                            {category.challenges}
                          </p>
                        )}
                      </div>
                    )}
                  </button>
                  
                  <div className={`
                    absolute bottom-0 left-0 right-0 
                    bg-[#F5F5F5] rounded-b-[5px]
                    transition-all duration-300 ease-in-out
                    ${isHovered ? 'h-full' : 'h-0'}
                    pointer-events-none
                    z-[-1]
                  `}/>
                </div>
              );
            })}
          </div>
          
          {/* Referral Section */}
          
          
          <SaveButtonWithModal onSave={handleSave} />
          
       
        

        </div>
      )}

    </div>
  );
};

export default GroupExperience;